import * as Sentry from "@sentry/react";
import axios, { AxiosError, AxiosResponse } from "axios";
import { findIndex, some } from "lodash/fp";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import { formattedListWithConjunction } from "@app/common/utils";
import { generateRandomString } from "@app/common/utils/randomUtils";
import Config from "@app/config";
import * as Arrays from "@app/utils/arrays";

import type { FileCandidate } from "@rui/FileUpload/types";

import { ALLOWED_TYPES, MAX_FILE_GB } from "./constants";
import type { Upload, UploadMetadata, UploadRequest } from "./types";
import { getFileDuration } from "./utils";

interface UploadState {
  uploads: Upload[];
  uploadRequests: { [key: string]: UploadRequest };
  createUpload: (file: FileCandidate) => void;
  cancelAllUploads: () => void;
  cancelUpload: (uploadId: string) => void;
  updateUploadMeta: (localId: string, options: UploadMetadata) => void;
  modal: { open: boolean; minimized: boolean };
  openModal: () => void;
  closeModal: () => void;
  clearUploads: () => void;
}

const uploadFile = (
  file: File,
  onUploadProgress: (percent: number) => void
): UploadRequest => {
  const formData = new FormData();
  formData.append("files", file);
  const controller = new AbortController();

  const response = axios.post(`${Config.api.HOST}/uploads`, formData, {
    withCredentials: true,
    signal: controller.signal,
    onUploadProgress: (progressEvent) => {
      const { loaded, total } = progressEvent;
      const percentage = Math.floor((loaded * 100) / total);
      onUploadProgress(percentage);
    },
  });

  return { response, abort: () => controller.abort() };
};

const startUpload = (
  upload: Upload,
  onUpdate: (upload: (uploadInState: Upload) => Upload) => void
) => {
  if (upload.status === "error") return;

  const uploadRequest = uploadFile(upload.file, (percent) => {
    // max progress is 99% to allow for response to complete
    onUpdate((uploadInState) => ({
      ...uploadInState,
      progress: Math.min(99, percent),
    }));
  });

  if (upload.duration == null) {
    getFileDuration(upload.file).then((duration) => {
      onUpdate((uploadInState) => ({
        ...uploadInState,
        duration: duration ? duration / 60 : null,
      }));
    });
  }

  uploadRequest.response
    .then((response: AxiosResponse) => {
      const json = response.data[0];
      const id = json.id;
      onUpdate((uploadInState) => ({
        ...uploadInState,
        id,
        progress: 100,
        status: "complete",
      }));
    })
    .catch((error: AxiosError) => {
      const response = error.response;
      let message =
        "Upload failed. Please try again or contact support@read.ai";
      let errorReason: Upload["errorReason"] = "unknown_error";

      if (response?.status === 402) {
        message = "Please purchase more credits to process this file";
        errorReason = "insufficient_credits";
      } else if (response?.status === 413) {
        message = `File exceeds the maximum file size of ${MAX_FILE_GB}GB`;
        errorReason = "too_large";
      } else if (response?.status === 415) {
        message = `File cannot be uploaded. Please try again with ${formattedListWithConjunction(
          ALLOWED_TYPES,
          "or"
        )} file types.`;
        errorReason = "not_supported";
      }

      Sentry.captureException(error, {
        extra: { ...response, message, errorReason },
      });

      onUpdate((uploadInState) => ({
        ...uploadInState,
        progress: 100,
        status: "error",
        message,
        errorReason,
      }));
    });

  return uploadRequest;
};

const replaceUpload = (
  uploads: Upload[],
  upload: Upload,
  updatedUpload: Upload
) => {
  const targetIndex = findIndex({ id: upload.id }, uploads);
  if (targetIndex === -1) return uploads;
  return Arrays.replaceAt(uploads, updatedUpload, targetIndex);
};

const replaceUploadRequest = (
  uploadRequests: UploadState["uploadRequests"],
  upload: Upload,
  updatedUpload: Upload
) => {
  if (upload.id === updatedUpload.id) return uploadRequests;

  const newUploadRequests = { ...uploadRequests };
  delete newUploadRequests[upload.id];
  newUploadRequests[updatedUpload.id] = uploadRequests[upload.id];
  return newUploadRequests;
};

const useUploadStore = createWithEqualityFn<UploadState>(
  (set) => ({
    uploads: [],
    uploadRequests: {},
    createUpload: (file) => {
      const id = generateRandomString(18);
      const upload: Upload = {
        id,
        localId: id,
        file: file.file,
        name: file.file.name,
        progress: file.isValid ? 0 : 100,
        duration: file.duration ?? null,
        status: file.isValid ? "loading" : "error",
        message: null,
        startTime: null,
        endTime: null,
        sessionId: null,
        sizeInBytes: file.file.size,
      };

      const uploadRequest = startUpload(upload, (updatedUploadFunc) => {
        set((state) => {
          const targetIndex = findIndex({ id: upload.id }, state.uploads);
          const uploadInState = state.uploads[targetIndex];
          const updatedUpload = updatedUploadFunc(uploadInState);
          const uploadWithEditedInfo = {
            ...updatedUpload,
            name: uploadInState?.name ?? updatedUpload.name,
            startTime: uploadInState?.startTime ?? updatedUpload.startTime,
          };

          return {
            uploads: replaceUpload(state.uploads, upload, uploadWithEditedInfo),
            uploadRequests: replaceUploadRequest(
              state.uploadRequests,
              upload,
              uploadWithEditedInfo
            ),
          };
        });
      });

      // TODO: limit number of simultaneous uploads?
      set((state) => ({
        uploads: [...state.uploads, upload],
        uploadRequests: uploadRequest
          ? { ...state.uploadRequests, [upload.id]: uploadRequest }
          : state.uploadRequests,
      }));
    },

    cancelUpload: (id) => {
      set((state) => {
        const targetIndex = findIndex({ id }, state.uploads);
        state.uploadRequests[id]?.abort();
        const newUploadRequests = { ...state.uploadRequests };
        delete newUploadRequests[id];

        return {
          uploads: Arrays.removeAt(state.uploads, targetIndex),
          uploadRequests: newUploadRequests,
        };
      });
    },
    cancelAllUploads: () => {
      set((state) => {
        Object.values(state.uploadRequests).forEach((uploadRequest) =>
          uploadRequest.abort()
        );
        return { uploads: [], uploadRequests: {} };
      });
    },
    updateUploadMeta: (localId, options) => {
      set((state) => {
        const targetIndex = findIndex({ localId }, state.uploads);
        const existingUpload = state.uploads[targetIndex];
        const newUpload = {
          ...existingUpload,
          name: options.title ?? existingUpload.name,
          startTime: options.startTime ?? existingUpload.startTime,
        };
        const updatedUploads = Arrays.replaceAt(
          state.uploads,
          newUpload,
          targetIndex
        );

        return {
          uploads: updatedUploads,
        };
      });
    },
    modal: { open: false, minimized: false },
    openModal: () => set({ modal: { open: true, minimized: false } }),
    closeModal: () => {
      set((state) => {
        if (some({ status: "loading" }, state.uploads)) {
          return {
            modal: { open: false, minimized: true },
          };
        }
        return { modal: { open: false, minimized: false } };
      });
    },
    clearUploads: () => {
      set({ uploads: [], uploadRequests: {} });
    },
  }),
  shallow
);

export default useUploadStore;
